import * as THREE from 'three';
import { InputManager } from '@/utils/InputManager';
import { ArrowType } from './ArrowType';
import { ArrowEntity } from './ArrowEntity';
import { ArrowPool } from './ArrowPool';
import { BowModel } from './BowModel';
import { EnemyManager } from '../enemies/EnemyManager';
import { store } from '@/store/store';
import { useArrow, setCurrentArrowType } from '@/store/playerSlice';
import { BowDrawingEvents } from '@/hooks/useBowDrawing';
import { PlayerController } from './PlayerController';

/**
 * Controls the bow and arrow mechanics
 */
export class BowController {
  // Bow model
  private bowModel: BowModel;
  
  // Arrow pool
  private arrowPool: ArrowPool;
  
  // Input manager
  private inputManager: InputManager;
  
  // Scene reference
  private scene: THREE.Scene;
  
  // Camera reference
  private camera: THREE.Camera;
  
  // Enemy manager for collision detection
  private enemyManager: EnemyManager | null = null;
  
  // Current arrow type
  private currentArrowType: ArrowType = ArrowType.NORMAL;
  
  // Drawing state
  private isDrawing: boolean = false;
  private drawStartTime: number = 0;
  private maxDrawTime: number = 1.5; // seconds
  
  // Reference to the player controller for velocity inheritance
  private playerController: PlayerController | null = null;
  
  /**
   * Creates a new BowController
   * @param scene The scene to add arrows to
   * @param camera The camera for arrow direction
   * @param inputManager The input manager
   */
  constructor(scene: THREE.Scene, camera: THREE.Camera, inputManager: InputManager) {
    this.scene = scene;
    this.camera = camera;
    this.inputManager = inputManager;
    
    // Create bow model
    this.bowModel = new BowModel();
    this.scene.add(this.bowModel.getObject());
    
    // Create arrow pool
    this.arrowPool = new ArrowPool(scene);
    
    // Get initial arrow type from Redux store
    const state = store.getState();
    if (state.player && state.player.currentArrowType !== undefined) {
      this.currentArrowType = state.player.currentArrowType;
    }
    
    // Setup input handlers
    this.setupInputHandlers();
    
    // Apply initial rotation to the bow
    // Rotate the bow 90 degrees to the left from its current position
    this.bowModel.getObject().rotation.y = Math.PI / 2;
    
    console.log("BowController initialized with adjusted bow orientation");
  }
  
  /**
   * Sets the enemy manager for arrow collision detection
   * @param enemyManager The enemy manager
   */
  public setEnemyManager(enemyManager: EnemyManager): void {
    this.enemyManager = enemyManager;
  }
  
  /**
   * Sets the player controller for velocity inheritance
   * @param playerController The player controller
   */
  public setPlayerController(playerController: PlayerController): void {
    this.playerController = playerController;
  }
  
  /**
   * Sets up input handlers for the bow
   */
  private setupInputHandlers(): void {
    try {
      // Mouse down to start drawing bow
      this.inputManager.onMouseDown((button, x, y) => {
        if (button === 0) { // Left mouse button
          this.startDrawing();
        }
      });
      
      // Mouse up to release arrow
      this.inputManager.onMouseUp((button, x, y) => {
        if (button === 0 && this.isDrawing) { // Left mouse button
          this.releaseArrow();
        }
      });
      
      // Key down to switch arrow types
      this.inputManager.onKeyDown((key) => {
        switch (key) {
          case '1':
            this.setArrowType(ArrowType.NORMAL);
            break;
          case '2':
            if (this.hasArrows(ArrowType.FIRE)) {
              this.setArrowType(ArrowType.FIRE);
            }
            break;
          case '3':
            if (this.hasArrows(ArrowType.EXPLOSIVE)) {
              this.setArrowType(ArrowType.EXPLOSIVE);
            }
            break;
          case '4':
            if (this.hasArrows(ArrowType.POISON)) {
              this.setArrowType(ArrowType.POISON);
            }
            break;
        }
      });
      
      console.log("Bow input handlers set up successfully");
    } catch (error) {
      console.error("Error setting up bow input handlers:", error);
    }
  }
  
  /**
   * Starts drawing the bow
   */
  private startDrawing(): void {
    try {
      // Check if we have arrows before starting to draw
      if (!this.hasArrows(this.currentArrowType)) {
        console.log(`No ${ArrowType[this.currentArrowType]} arrows left`);
        return;
      }
      
      // Don't start drawing if already drawing
      if (this.isDrawing) {
        console.log('[BowController] Already drawing, ignoring startDrawing call');
        return;
      }
      
      this.isDrawing = true;
      this.drawStartTime = performance.now() / 1000;
      this.bowModel.startDrawing();
      
      // Dispatch bow drawing start event
      BowDrawingEvents.startDrawing(this.maxDrawTime);
      
      console.log('[BowController] Started drawing bow');
    } catch (error) {
      console.error('[BowController] Error in startDrawing method:', error);
      // Reset drawing state in case of error
      this.isDrawing = false;
    }
  }
  
  /**
   * Releases the arrow
   */
  private releaseArrow(): void {
    try {
      // Don't do anything if not drawing
      if (!this.isDrawing) {
        console.log('[BowController] Not drawing, ignoring releaseArrow call');
        return;
      }
      
      // Calculate draw strength based on time
      const currentTime = performance.now() / 1000;
      const drawTime = Math.min(currentTime - this.drawStartTime, this.maxDrawTime);
      const drawStrength = drawTime / this.maxDrawTime;
      
      console.log('[BowController] Releasing arrow with draw strength:', drawStrength);
      
      // Dispatch bow drawing end event
      BowDrawingEvents.endDrawing(drawStrength);
      
      // Get arrow direction from camera
      const direction = new THREE.Vector3(0, 0, -1);
      direction.applyQuaternion(this.camera.quaternion);
      
      // Get arrow position (slightly in front of camera)
      const position = new THREE.Vector3();
      position.copy(this.camera.position);
      position.add(direction.clone().multiplyScalar(0.5));
      
      // Check if we have arrows of this type
      if (this.hasArrows(this.currentArrowType)) {
        // Get the arrow from the pool
        const arrow = this.arrowPool.getArrow(this.currentArrowType);
        
        // Calculate base arrow velocity
        const baseVelocity = direction.clone().multiplyScalar(drawStrength * 30);
        
        // Add player's velocity to the arrow if available
        if (this.playerController) {
          // Get player velocity
          const playerVelocity = this.playerController.getVelocity();
          
          // Add player velocity to the arrow's velocity
          baseVelocity.add(playerVelocity);
          
          console.log(`Adding player velocity (${playerVelocity.x.toFixed(2)}, ${playerVelocity.y.toFixed(2)}, ${playerVelocity.z.toFixed(2)}) to arrow`);
        }
        
        // Calculate the new direction and speed
        const finalSpeed = baseVelocity.length();
        const finalDirection = baseVelocity.clone().normalize();
        
        // Fire the arrow with the combined velocity
        arrow.fire(position, finalDirection, finalSpeed, drawStrength);
        
        // Use an arrow from inventory in Redux
        store.dispatch(useArrow(this.currentArrowType));
        
        console.log(`Released ${ArrowType[this.currentArrowType]} arrow with strength ${drawStrength.toFixed(2)} and speed ${finalSpeed.toFixed(2)}`);
      } else {
        console.log(`No ${ArrowType[this.currentArrowType]} arrows left`);
      }
      
      // Reset bow
      this.isDrawing = false;
      this.bowModel.releaseArrow();
    } catch (error) {
      console.error('[BowController] Error in releaseArrow method:', error);
      // Reset drawing state in case of error
      this.isDrawing = false;
      this.bowModel.releaseArrow();
    }
  }
  
  /**
   * Sets the arrow type
   * @param type The arrow type to set
   */
  public setArrowType(type: ArrowType): void {
    if (this.currentArrowType !== type) {
      this.currentArrowType = type;
      console.log(`Switched to ${ArrowType[type]} arrows`);
      
      // Update Redux store
      store.dispatch(setCurrentArrowType(type));
    }
  }
  
  /**
   * Checks if there are arrows of the specified type
   * @param type The arrow type to check
   * @returns True if there are arrows of the specified type
   */
  public hasArrows(type: ArrowType): boolean {
    const state = store.getState();
    if (!state.player || !state.player.arrowInventory) return false;
    
    const count = state.player.arrowInventory[type];
    return count === -1 || count > 0; // -1 means unlimited
  }
  
  /**
   * Gets the number of arrows of the specified type
   * @param type The arrow type to check
   * @returns The number of arrows, or -1 for unlimited
   */
  public getArrowCount(type: ArrowType): number {
    const state = store.getState();
    if (!state.player || !state.player.arrowInventory) return 0;
    
    return state.player.arrowInventory[type] || 0;
  }
  
  /**
   * Updates the bow controller
   * @param deltaTime Time since last update in seconds
   */
  public update(deltaTime: number): void {
    try {
      // Update bow model
      if (this.isDrawing) {
        const currentTime = performance.now() / 1000;
        const drawTime = Math.min(currentTime - this.drawStartTime, this.maxDrawTime);
        const drawProgress = drawTime / this.maxDrawTime;
        
        // Update bow model
        this.bowModel.setDrawProgress(drawProgress);
        
        // Dispatch bow drawing update event
        // Only dispatch if there's a meaningful change to avoid flooding with events
        if (drawProgress > 0) {
          BowDrawingEvents.updateDrawing(drawProgress, this.currentArrowType);
        }
      }
      
      // Update bow position and rotation to match camera
      this.updateBowPosition();
      
      // Get enemies for collision detection
      const enemies = this.enemyManager ? this.enemyManager.getEnemies() : [];
      
      // Update arrows
      this.arrowPool.update(deltaTime, enemies);
    } catch (error) {
      console.error('[BowController] Error in update method:', error);
    }
  }
  
  /**
   * Updates the bow position and rotation to match camera
   */
  private updateBowPosition(): void {
    // Position bow in front and to the right of camera
    // Increased X value from 0.3 to 0.4 to move it more to the right
    // Adjusted Z value from -0.5 to -0.6 to move it slightly forward
    const bowPosition = new THREE.Vector3(0.4, -0.3, -0.6);
    bowPosition.applyQuaternion(this.camera.quaternion);
    bowPosition.add(this.camera.position);
    
    // Set bow position
    this.bowModel.getObject().position.copy(bowPosition);
    
    // Set bow rotation to match camera but with additional rotation for correct orientation
    const bowRotation = new THREE.Quaternion().copy(this.camera.quaternion);
    
    // Apply the rotation to the bow
    this.bowModel.getObject().quaternion.copy(bowRotation);
    
    // Apply an additional rotation to orient the bow correctly
    // This rotates the bow 90 degrees around the local Y axis in the opposite direction
    const additionalRotation = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), -Math.PI / 2);
    this.bowModel.getObject().quaternion.multiply(additionalRotation);
  }
  
  /**
   * Gets the current arrow type
   * @returns The current arrow type
   */
  public getCurrentArrowType(): ArrowType {
    return this.currentArrowType;
  }
  
  /**
   * Cleans up resources when the bow controller is no longer needed
   */
  public dispose(): void {
    // Remove bow from scene
    this.scene.remove(this.bowModel.getObject());
    
    // Dispose of arrow pool
    this.arrowPool.dispose();
    
    // Dispose of bow model
    this.bowModel.dispose();
  }
} 